// ./src/components/features/Connectors/ConfigEditors/AzureTableConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Stack, TextInput, Text, Anchor, Alert, Code } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AzureTableConnectorConfig } from "@/types";

export interface AzureTableConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AzureTableConnectorConfig = {
  connectionString: "",
  tableName: "",
  partitionKey: "{{formId}}",
  rowKey: "{{requestId}}",
};

/**
 * Parse and validate Azure Table connector configuration
 */
const parseAzureTableConfig = (
  jsonString: string
): AzureTableConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const AzureTableConnectorConfigEditor: React.FC<
  AzureTableConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AzureTableConnectorConfig>(() =>
    parseAzureTableConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAzureTableConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AzureTableConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle connection string change
  const handleConnectionStringChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, connectionString: e.target.value });
  };

  // Handle table name change
  const handleTableNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Azure table names must start with a letter, alphanumeric only
    const tableName = e.target.value.replace(/[^A-Za-z0-9]/g, "");
    updateConfig({ ...config, tableName });
  };

  // Handle partition key change
  const handlePartitionKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, partitionKey: e.target.value });
  };

  // Handle row key change
  const handleRowKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, rowKey: e.target.value });
  };

  // Validate table name
  const tableNameError =
    config.tableName && !/^[A-Za-z][A-Za-z0-9]{2,62}$/.test(config.tableName)
      ? "Table name must be 3-63 characters, start with a letter, alphanumeric only"
      : undefined;

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Find your connection string in{" "}
          <Anchor
            href="https://portal.azure.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Azure Portal
          </Anchor>
          {" → Storage Account → Access keys → Connection string"}
        </Text>
      </Alert>

      {/* Connection String Field */}
      <TextInput
        label="Connection String"
        placeholder="DefaultEndpointsProtocol=https;AccountName=..."
        description="Azure Storage account connection string"
        value={config.connectionString}
        onChange={handleConnectionStringChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Table Name Field */}
      <TextInput
        label="Table Name"
        placeholder="FormSubmissions"
        description="Name of the table (will be created if it doesn't exist)"
        value={config.tableName}
        onChange={handleTableNameChange}
        disabled={disabled}
        required
        size="md"
        error={tableNameError}
      />

      {/* Partition Key Field */}
      <TextInput
        label="Partition Key Pattern"
        placeholder="{{formId}}"
        description="Groups related submissions for efficient querying"
        value={config.partitionKey || "{{formId}}"}
        onChange={handlePartitionKeyChange}
        disabled={disabled}
        size="md"
      />

      {/* Row Key Field */}
      <TextInput
        label="Row Key Pattern"
        placeholder="{{requestId}}"
        description="Unique identifier within a partition"
        value={config.rowKey || "{{requestId}}"}
        onChange={handleRowKeyChange}
        disabled={disabled}
        size="md"
      />

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
          <br />
          <Code>{"{{randUuid}}"}</Code> — Random UUID
        </Text>
      </Alert>
    </Stack>
  );
};
